import { Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { CalendarFilters, CalendarMemberOption, CalendarTeamOption } from '@/types/work'

// Sentinel matching CrmWork::CalendarsController::ASSIGNEE_NONE — selects
// unassigned team tasks.
export const ASSIGNEE_NONE = 'none'

interface CalendarFiltersBarProps {
  teams: CalendarTeamOption[]
  members: CalendarMemberOption[]
  filters: CalendarFilters
  onTeamChange: (teamId: number) => void
  onAssigneeChange: (assigneeIds: string[]) => void
}

// Team picker (one team at a time, perimeter-wide, depth-indented) and
// member multi-select with a "Non assigné" entry. Dedicated component —
// NOT an extension of components/TeamPicker.tsx, which reads direct
// memberships from shared auth props; this one renders the full
// server-supplied visibility hierarchy.
export function CalendarFiltersBar({
  teams,
  members,
  filters,
  onTeamChange,
  onAssigneeChange,
}: CalendarFiltersBarProps) {
  const selected = filters.assignee_id
  const selectedCount = selected.length

  function toggle(value: string) {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]
    onAssigneeChange(next)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={filters.team_id !== null ? String(filters.team_id) : undefined}
        onValueChange={(v) => {
          const id = Number(v)
          if (Number.isFinite(id) && id !== filters.team_id) onTeamChange(id)
        }}
      >
        <SelectTrigger className="h-9 w-[240px]" aria-label="Équipe">
          <SelectValue placeholder="Équipe" />
        </SelectTrigger>
        <SelectContent>
          {teams.map((team) => (
            <SelectItem key={team.id} value={String(team.id)}>
              {/* Hierarchy indentation via non-breaking padding — depth
                  comes from the server's pre-order flatten. */}
              <span style={{ paddingLeft: `${team.depth * 0.75}rem` }}>{team.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Stays enabled even when the team has no primary members — the
          "Non assigné" entry is always applicable (unassigned tasks can
          exist on a member-less team, and a deep-linked `none` selection
          must remain clearable). */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            <Users className="mr-1.5 h-4 w-4" />
            Membres
            {selectedCount > 0 ? ` (${selectedCount})` : ''}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>Filtrer par membre</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {members.map((member) => (
            <DropdownMenuCheckboxItem
              key={member.id}
              checked={selected.includes(String(member.id))}
              onCheckedChange={() => toggle(String(member.id))}
              // Keep the menu open for multi-toggling.
              onSelect={(e) => e.preventDefault()}
            >
              {member.label}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={selected.includes(ASSIGNEE_NONE)}
            onCheckedChange={() => toggle(ASSIGNEE_NONE)}
            onSelect={(e) => e.preventDefault()}
          >
            Non assigné
          </DropdownMenuCheckboxItem>
          {selectedCount > 0 ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem checked={false} onSelect={() => onAssigneeChange([])}>
                Tout afficher
              </DropdownMenuCheckboxItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {members.length === 0 ? (
        <p className="text-xs text-muted-foreground">Cette équipe n’a pas de membres.</p>
      ) : null}
    </div>
  )
}
